import React, { useRef, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'; // Added useLocation
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Container } from '@/components/ui/container';
import AuthSection from '@/components/auth/AuthSection';
import { Helmet } from 'react-helmet';
import { useToast } from '@/hooks/toast/index';

const Auth = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation(); // Added useLocation
  const { toast } = useToast();
  const toastsShownRef = useRef<Set<string>>(new Set());
  const isMountedRef = useRef(true);
  const isNavigatingRef = useRef(false);
  
  useEffect(() => {
    isMountedRef.current = true;
    isNavigatingRef.current = false;
    
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  
  // Redirect if already authenticated
  useEffect(() => {
    if (!isMountedRef.current || isNavigatingRef.current) return;
    
    if (isAuthenticated) {
      isNavigatingRef.current = true;
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);
  
  // Process query parameters and hash fragments
  useEffect(() => {
    if (!isMountedRef.current || isNavigatingRef.current) return;
    
    const typeFromQuery = searchParams.get('type');
    
    // Show notifications for specific redirect reasons from query params
    if (typeFromQuery && !toastsShownRef.current.has(typeFromQuery)) {
      toastsShownRef.current.add(typeFromQuery);
      setTimeout(() => {
        if (!isMountedRef.current || isNavigatingRef.current) return;
        if (typeFromQuery === 'recovery') { // This toast might be redundant if user is immediately sent to /reset-password via RecoveryCodeHandler
          // toast({
          //   title: "Email konfirmimi u dërgua",
          //   description: "Ju lutemi kontrolloni email-in tuaj për të rivendosur fjalëkalimin.",
          // });
        } else if (typeFromQuery === 'signup') {
          toast({
            title: "Email konfirmimi u dërgua",
            description: "Ju lutemi konfirmoni emailin tuaj për të aktivizuar llogarinë.",
          });
        } else if (typeFromQuery === 'reset_sent') {
          // This might be the PasswordResetEmailSent page. If user lands here, it's ok.
        } else if (typeFromQuery === 'reset_success') {
          // This might be after a successful reset on ResetPassword page.
          toast({
            title: "Fjalëkalimi u rivendos me sukses",
            description: "Tani mund të hyni me fjalëkalimin e ri.",
          });
        }
      }, 100);
    }

    // Handle hash fragments for recovery (PKCE code or older access_token)
    const hash = location.hash;
    if (hash && hash.includes('type=recovery')) {
      const hashParams = new URLSearchParams(hash.substring(1));
      if (hashParams.get('code') || hashParams.get('access_token')) {
        console.log("Auth.tsx: Detected recovery params in HASH. Redirecting to /reset-password.");
        isNavigatingRef.current = true;
        navigate('/reset-password' + hash, { replace: true });
        return; // Exit early if redirected
      }
    }

    // Handle search parameters for recovery (PKCE code)
    const search = location.search;
    const searchParamsFromLocation = new URLSearchParams(search);
    if (searchParamsFromLocation.get('code') && searchParamsFromLocation.get('type') === 'recovery') {
        console.log("Auth.tsx: Detected recovery params in SEARCH. Redirecting to /reset-password.");
        isNavigatingRef.current = true;
        navigate('/reset-password' + search, { replace: true });
        return; // Exit early if redirected
    }

  }, [searchParams, location, navigate, toast]);

  return (
    <div className="flex flex-col min-h-screen">
      <Helmet>
        <title>Hyrje dhe Regjistrim | TreguIm - Platforma Online e Shqipërisë</title>
        <meta name="description" content="Hyni në llogarinë tuaj ose regjistrohuni në TreguIm për të aksesuar të gjitha veçoritë e platformës sonë, për të publikuar shpallje dhe menaxhuar blerjet." />
        <meta name="keywords" content="hyrje treguim.org, regjistrim treguim.org, llogari shqiptare, identifikim, login" />
        <meta property="og:title" content="Hyrje dhe Regjistrim | TreguIm" />
        <meta property="og:description" content="Hyni në llogarinë tuaj ose regjistrohuni në TreguIm për të aksesuar platformën." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://treguim.org/auth" />
        <meta property="og:image" content="https://treguim.org/logo512.png" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Hyrje dhe Regjistrim | TreguIm" />
        <meta name="twitter:description" content="Hyni në llogarinë tuaj ose regjistrohuni në TreguIm." />
        <link rel="canonical" href="https://treguim.org/auth" />
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <Navbar />
      
      <main className="flex-grow py-16 bg-gray-50">
        <Container>
          <AuthSection />
        </Container>
      </main>
      
      <Footer />
    </div>
  );
};

export default Auth;
